import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Modal, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Meter, useGetMeterHistoricByImeiAndStartDateEndDateQuery } from "../../redux/residentPortalApi";
import dayjs from "dayjs";
import { makeStyles } from "@mui/styles";
import { theme } from "../../theme/theme";
import { capitalise } from "../../utils/stringUtil";

const useStyles = makeStyles({
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    maxHeight: '80%',
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  chartContainer: {
    marginTop: theme.spacing(2),
  },
  toggleButton: {
    marginBottom: theme.spacing(2),
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px', // spinner height placeholder
  },
});

interface MeterDetailsModelProps {
  open: boolean;
  onClose: () => void;
  meter: Meter | null;
}

const MeterDetailsModel: React.FC<MeterDetailsModelProps> = ({ open, onClose, meter }) => {
  const classes = useStyles();
  const [view, setView] = useState('weekly');
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: string) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const today = dayjs().format('YYYY-MM-DD');
  const startDate = dayjs().subtract(
    view === 'yearly' ? 1 : view === '3months' ? 3 : view === 'monthly' ? 1 : 7,
    view === 'yearly' ? 'year' : view === '3months' ? 'month' : view === 'monthly' ? 'month' : 'day'
  ).format('YYYY-MM-DD');

  const { data: meterHistoricData, isLoading: isLoadingHistoric } = useGetMeterHistoricByImeiAndStartDateEndDateQuery({
    imei: meter?.imei || '',
    startDate: startDate,
    endDate: today,
  });

  // Update filteredData whenever meterHistoricData or view changes
  useEffect(() => {
    if (meterHistoricData) {
      const newData = meterHistoricData.filter((entry: any) => {
        if (view === 'yearly') {
          return dayjs(entry.readTime).date() === 1;
        } else if (view === '3months') {
          return dayjs(entry.readTime).date() === 1;
        } else if (view === 'monthly') {
          return dayjs(entry.readTime).day() === 1;
        } else {
          return true;
        }
      });
      setFilteredData(newData);
    }
  }, [meterHistoricData, view]);

  const formatXAxis = (tickItem: string) => {
    if (view === 'yearly') {
      return dayjs(tickItem).format('MMM YYYY');
    } else if (view === '3months') {
      return dayjs(tickItem).format('MMM DD');
    } else if (view === 'monthly') {
      return dayjs(tickItem).format('DD MMM');
    } else {
      return dayjs(tickItem).format('ddd, DD MMM');
    }
  };

  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const date = dayjs(payload[0].payload.readTime).format('dddd, MMMM D, YYYY');
      const unit = meter?.service === 'electricity' ? 'kWh' : 'm³';
      const consumption = `${payload[0].value} ${unit}`;

      return (
        <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px', borderRadius: '5px' }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#3f51b5' }}>{date}</p>
          <p style={{ margin: 0 }}>Consumption: <span style={{ color: '#3f51b5' }}>{consumption}</span></p>
        </div>
      );
    }
    return null;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.modalStyle}>
        <Typography variant="h6" gutterBottom>
          {capitalise(meter?.service ?? '')} Meter Details
        </Typography>

        {isLoadingHistoric ? (
          <Box className={classes.loadingSpinner}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              className={classes.toggleButton}
              aria-label="view toggle"
            >
              <ToggleButton value="weekly" aria-label="weekly view">Weekly</ToggleButton>
              <ToggleButton value="monthly" aria-label="monthly view">Monthly</ToggleButton>
              <ToggleButton value="3months" aria-label="3 months view">3 Months</ToggleButton>
              <ToggleButton value="yearly" aria-label="yearly view">Yearly</ToggleButton>
            </ToggleButtonGroup>

            <Box className={classes.chartContainer}>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={filteredData}>
                  <CartesianGrid stroke="#e0e0e0" />
                  <XAxis dataKey="readTime" tickFormatter={formatXAxis} tick={{ fontSize: 12 }} />
                  <YAxis
                    yAxisId="left"
                    label={{ value: `Consumption (${meter?.service === 'electricity' ? 'kWh' : 'm³'})`, angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <Tooltip content={customTooltip} />
                  <Legend verticalAlign="top" wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="consumption"
                    stroke="#3f51b5"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}

        <Button onClick={onClose} sx={{ marginTop: 2 }}>Close</Button>
      </Box>
    </Modal>
  );
};

export default MeterDetailsModel;
